import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ColumnSchema, ResultRow } from '@/types/api';
import type { FilterSpec, SortDirection, SortSpec } from '@/cache/types';
import { classifyType } from '@/lib/values';
import { GridCell } from './GridCell';
import { ColumnMenu } from './ColumnMenu';
import { CellDetail } from './CellDetail';
import {
  HEADER_HEIGHT,
  ROW_HEIGHT,
  ROW_NUMBER_WIDTH,
  clampColumnWidth,
  moveColumn,
  visibleColumns,
  type ColumnWidths,
} from './gridModel';

export interface ResultGridProps {
  readonly columns: readonly ColumnSchema[];
  readonly rows: readonly ResultRow[];
  readonly totalRows: number;
  readonly sort: readonly SortSpec[];
  readonly filters: readonly FilterSpec[];
  readonly columnOrder: readonly string[];
  readonly hiddenColumns: readonly string[];
  readonly onSortChange: (sort: readonly SortSpec[]) => void;
  readonly onFiltersChange: (filters: readonly FilterSpec[]) => void;
  readonly onColumnOrderChange: (order: readonly string[]) => void;
  readonly onToggleColumn: (name: string) => void;
  /** Notifies the Column Explorer which column the user is looking at. */
  readonly onFocusColumn?: (name: string) => void;
  /** Opens the Filter & Sort panel seeded with a column. */
  readonly onFilterInPanel?: (name: string) => void;
  readonly columnWidths?: ColumnWidths;
  readonly onColumnWidthsChange?: (widths: ColumnWidths) => void;
}

interface CellRef {
  readonly row: number;
  readonly col: number;
}

const TYPE_BADGE: Record<string, string> = {
  number: '#',
  bigint: '#',
  boolean: '01',
  temporal: '🕐',
  nested: '{}',
  binary: '0x',
  string: 'ᴀʙᴄ',
};

export function ResultGrid({
  columns,
  rows,
  totalRows,
  sort,
  filters,
  columnOrder,
  hiddenColumns,
  onSortChange,
  onFiltersChange,
  onColumnOrderChange,
  onToggleColumn,
  onFocusColumn,
  onFilterInPanel,
  columnWidths,
  onColumnWidthsChange,
}: ResultGridProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [menuColumn, setMenuColumn] = useState<string | null>(null);
  const [selected, setSelected] = useState<CellRef | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const menuAnchors = useRef(new Map<string, HTMLButtonElement | null>());

  // Live width while dragging, so the resize follows the pointer without
  // writing to persisted state on every mouse move.
  const [drag, setDrag] = useState<{ column: string; width: number } | null>(null);

  const effectiveWidths = useMemo<ColumnWidths>(
    () => (drag ? { ...columnWidths, [drag.column]: drag.width } : (columnWidths ?? {})),
    [columnWidths, drag],
  );

  const visible = useMemo(
    () => visibleColumns(columns, columnOrder, hiddenColumns, effectiveWidths),
    [columns, columnOrder, hiddenColumns, effectiveWidths],
  );

  const sortByColumn = useMemo(
    () => new Map(sort.map((spec) => [spec.column, spec.direction])),
    [sort],
  );
  const filterByColumn = useMemo(
    () => new Map(filters.map((spec) => [spec.column, spec])),
    [filters],
  );

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: visible.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => visible[index]?.width ?? 160,
    overscan: 4,
  });

  useEffect(() => {
    columnVirtualizer.measure();
  }, [columnVirtualizer, effectiveWidths]);

  const startResize = useCallback(
    (event: React.PointerEvent, column: string, startWidth: number): void => {
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;

      const onMove = (move: PointerEvent): void => {
        setDrag({ column, width: clampColumnWidth(startWidth + (move.clientX - startX)) });
      };
      const onUp = (up: PointerEvent): void => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        const finalWidth = clampColumnWidth(startWidth + (up.clientX - startX));
        setDrag(null);
        onColumnWidthsChange?.({ ...columnWidths, [column]: finalWidth });
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [columnWidths, onColumnWidthsChange],
  );

  const totalWidth = columnVirtualizer.getTotalSize() + ROW_NUMBER_WIDTH;

  const applySort = useCallback(
    (name: string, direction: SortDirection | null) => {
      onSortChange(direction === null ? [] : [{ column: name, direction }]);
    },
    [onSortChange],
  );

  const applyFilter = useCallback(
    (name: string, filter: FilterSpec | null) => {
      const rest = filters.filter((entry) => entry.column !== name);
      onFiltersChange(filter ? [...rest, filter] : rest);
    },
    [filters, onFiltersChange],
  );

  const selectCell = useCallback(
    (row: number, col: number) => {
      setSelected({ row, col });
      const target = visible[col];
      if (target && onFocusColumn) onFocusColumn(target.column.name);
    },
    [visible, onFocusColumn],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!selected) return;
      const { row, col } = selected;
      let next: CellRef | null = null;

      switch (event.key) {
        case 'ArrowDown':
          next = { row: Math.min(rows.length - 1, row + 1), col };
          break;
        case 'ArrowUp':
          next = { row: Math.max(0, row - 1), col };
          break;
        case 'ArrowRight':
          next = { row, col: Math.min(visible.length - 1, col + 1) };
          break;
        case 'ArrowLeft':
          next = { row, col: Math.max(0, col - 1) };
          break;
        case 'Home':
          next = { row, col: 0 };
          break;
        case 'End':
          next = { row, col: visible.length - 1 };
          break;
        case 'PageDown':
          next = { row: Math.min(rows.length - 1, row + 25), col };
          break;
        case 'PageUp':
          next = { row: Math.max(0, row - 25), col };
          break;
        case 'Escape':
          setSelected(null);
          return;
        default:
          return;
      }

      event.preventDefault();
      if (next) {
        selectCell(next.row, next.col);
        rowVirtualizer.scrollToIndex(next.row, { align: 'auto' });
        columnVirtualizer.scrollToIndex(next.col, { align: 'auto' });
      }
    },
    [selected, rows.length, visible.length, selectCell, rowVirtualizer, columnVirtualizer],
  );

  const selectedColumn = selected ? visible[selected.col]?.column : undefined;
  const selectedValue =
    selected && selectedColumn
      ? (rows[selected.row]?.[visible[selected.col]?.sourceIndex ?? 0] ?? null)
      : null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div
        ref={scrollRef}
        className="min-h-0 min-w-0 flex-1 overflow-auto outline-none"
        role="grid"
        aria-label="Query result"
        aria-rowcount={totalRows}
        aria-colcount={visible.length + 1}
        tabIndex={0}
        onKeyDown={onKeyDown}
        data-testid="result-grid"
      >
        <div style={{ width: totalWidth, position: 'relative' }}>
          {/* Header */}
          <div
            role="row"
            aria-rowindex={1}
            className="sticky top-0 z-20 flex border-b border-line-strong bg-raised"
            style={{ height: HEADER_HEIGHT, width: totalWidth }}
          >
            <div
              role="columnheader"
              aria-colindex={1}
              className="eyebrow sticky left-0 z-10 flex items-center justify-end border-r border-line bg-raised px-2"
              style={{ width: ROW_NUMBER_WIDTH, minWidth: ROW_NUMBER_WIDTH }}
            >
              #
            </div>

            {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
              const entry = visible[virtualColumn.index];
              if (!entry) return null;
              const { column } = entry;
              const direction = sortByColumn.get(column.name) ?? null;
              const filter = filterByColumn.get(column.name) ?? null;
              const badge = TYPE_BADGE[classifyType(column.type)] ?? 'A';

              return (
                <div
                  key={column.name}
                  role="columnheader"
                  data-column={column.name}
                  aria-colindex={virtualColumn.index + 2}
                  aria-sort={
                    direction === 'asc'
                      ? 'ascending'
                      : direction === 'desc'
                        ? 'descending'
                        : 'none'
                  }
                  className="absolute top-0 flex h-full items-center border-r border-line"
                  style={{
                    left: virtualColumn.start + ROW_NUMBER_WIDTH,
                    width: virtualColumn.size,
                  }}
                  draggable
                  onDragStart={() => setDragging(column.name)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (dragging && dragging !== column.name) {
                      const order =
                        columnOrder.length > 0 ? columnOrder : columns.map((c) => c.name);
                      onColumnOrderChange(moveColumn(order, dragging, column.name));
                    }
                    setDragging(null);
                  }}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 flex-col justify-center px-2 text-left transition-colors hover:text-ink"
                    aria-label={`Sort by ${column.name}`}
                    onClick={() =>
                      applySort(column.name, direction === 'asc' ? 'desc' : direction === 'desc' ? null : 'asc')
                    }
                    title={`${column.name} · ${column.typeText}
Click to sort`}
                  >
                    <span className="flex min-w-0 items-center gap-1">
                      <span className="truncate font-mono text-[11px] leading-[13px] text-ink">
                        {column.name}
                      </span>
                      {direction ? (
                        <span className="shrink-0 text-[10px] text-local" aria-hidden="true">
                          {direction === 'asc' ? '↑' : '↓'}
                        </span>
                      ) : null}
                      {filter ? (
                        <span
                          className="shrink-0 text-[10px] text-remote"
                          title="Filtered"
                          aria-hidden="true"
                        >
                          ⏷
                        </span>
                      ) : null}
                    </span>
                    <span className="flex min-w-0 items-center gap-1 text-faint">
                      <span className="shrink-0 text-[9px] leading-[11px]" aria-hidden="true">
                        {badge}
                      </span>
                      <span className="truncate text-[9px] leading-[11px]">
                        {column.typeText.toLowerCase()}
                      </span>
                    </span>
                  </button>

                  <div>
                    <button
                      ref={(node) => {
                        menuAnchors.current.set(column.name, node);
                      }}
                      type="button"
                      className="icon-btn mr-0.5 h-5"
                      aria-label={`Options for ${column.name}`}
                      aria-haspopup="menu"
                      aria-expanded={menuColumn === column.name}
                      onClick={() =>
                        setMenuColumn((current) => (current === column.name ? null : column.name))
                      }
                    >
                      ⋮
                    </button>
                    {menuColumn === column.name ? (
                      <ColumnMenu
                        column={column}
                        sortDirection={direction}
                        filter={filter}
                        onSort={(next) => applySort(column.name, next)}
                        onFilter={(next) => applyFilter(column.name, next)}
                        onHide={() => onToggleColumn(column.name)}
                        onClose={() => setMenuColumn(null)}
                        anchorRef={{ current: menuAnchors.current.get(column.name) ?? null }}
                        onFilterInPanel={
                          onFilterInPanel ? () => onFilterInPanel(column.name) : undefined
                        }
                      />
                    ) : null}
                  </div>

                  {onColumnWidthsChange ? (
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${column.name}`}
                      data-testid={`resize-${column.name}`}
                      title={`Drag to resize. Double-click to reset ${column.name}.`}
                      className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-local"
                      onPointerDown={(event) =>
                        startResize(event, column.name, entry.width)
                      }
                      onDoubleClick={() => {
                        const next = { ...columnWidths };
                        delete next[column.name];
                        onColumnWidthsChange(next);
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Body */}
          <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index];
              if (!row) return null;

              return (
                <div
                  key={virtualRow.key}
                  role="row"
                  aria-rowindex={virtualRow.index + 2}
                  className="absolute left-0 flex border-b border-line hover:bg-raised"
                  style={{
                    top: virtualRow.start,
                    height: virtualRow.size,
                    width: totalWidth,
                  }}
                >
                  <div
                    role="rowheader"
                    aria-colindex={1}
                    className="tnum sticky left-0 z-10 flex items-center justify-end border-r border-line bg-surface px-2 text-[11px] text-faint"
                    style={{ width: ROW_NUMBER_WIDTH, minWidth: ROW_NUMBER_WIDTH }}
                  >
                    {virtualRow.index + 1}
                  </div>

                  {columnVirtualizer.getVirtualItems().map((virtualColumn) => {
                    const entry = visible[virtualColumn.index];
                    if (!entry) return null;
                    const isSelected =
                      selected?.row === virtualRow.index && selected.col === virtualColumn.index;

                    return (
                      <div
                        key={entry.column.name}
                        className="absolute top-0 h-full"
                        style={{
                          left: virtualColumn.start + ROW_NUMBER_WIDTH,
                          width: virtualColumn.size,
                        }}
                      >
                        <GridCell
                          value={row[entry.sourceIndex] ?? null}
                          type={entry.column.type}
                          width={virtualColumn.size}
                          rowIndex={virtualRow.index}
                          colIndex={virtualColumn.index}
                          selected={isSelected}
                          focused={isSelected}
                          onSelect={selectCell}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {selected && selectedColumn ? (
        <CellDetail
          column={selectedColumn}
          value={selectedValue}
          rowIndex={selected.row}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}
